import React, { useEffect, useState } from 'react';
import {
    Globe, RefreshCw, Zap, Megaphone, LineChart, Repeat, HelpCircle, X,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { fetchPortalAttributionAnalytics } from '../api/analyticsApi';

const formatCurrency = (val) => {
    if (val === null || val === undefined || Number.isNaN(Number(val))) return '—';
    return new Intl.NumberFormat('en-AU', {
        style: 'currency',
        currency: 'AUD',
        maximumFractionDigits: 0,
    }).format(Number(val));
};

/**
 * The four attribution categories, in narrative strength order, plus the
 * unclassified remainder. `key` matches the backend category keys so the
 * breakdown always reconciles to Total DOMA Influenced Commission.
 */
const CATEGORIES = [
    {
        key: 'direct',
        label: 'Direct Portal Sales',
        blurb: 'Buyer discovered & purchased the property inside the portal.',
        icon: Zap,
        tint: 'text-teal-600 bg-teal-50',
        ring: 'border-teal-200',
        ringActive: 'border-teal-500 ring-2 ring-teal-200',
        bar: 'bg-teal-500',
    },
    {
        key: 'assisted',
        label: 'Portal Assisted Sales',
        blurb: 'Buyer came from another source but the portal influenced the deal.',
        icon: Megaphone,
        tint: 'text-indigo-600 bg-indigo-50',
        ring: 'border-indigo-200',
        ringActive: 'border-indigo-500 ring-2 ring-indigo-200',
        bar: 'bg-indigo-500',
    },
    {
        key: 'analytics',
        label: 'Analytics Influenced Sales',
        blurb: 'Portal surfaced the buyer’s intent and an agent followed up.',
        icon: LineChart,
        tint: 'text-amber-600 bg-amber-50',
        ring: 'border-amber-200',
        ringActive: 'border-amber-500 ring-2 ring-amber-200',
        bar: 'bg-amber-500',
    },
    {
        key: 'returning',
        label: 'Returning Buyer Sales',
        blurb: 'A prior Collings buyer purchased again while still engaged.',
        icon: Repeat,
        tint: 'text-emerald-600 bg-emerald-50',
        ring: 'border-emerald-200',
        ringActive: 'border-emerald-500 ring-2 ring-emerald-200',
        bar: 'bg-emerald-500',
    },
];

const UNCLASSIFIED = {
    key: 'unclassified',
    label: 'Unclassified',
    blurb: 'Portal-attributed but the buyer couldn’t be matched to portal activity.',
    bar: 'bg-gray-300',
};

const CATEGORY_LABELS = {
    direct: 'Direct',
    assisted: 'Assisted',
    analytics: 'Analytics',
    returning: 'Returning',
    unclassified: 'Unclassified',
};

const CATEGORY_BADGE = {
    direct: 'bg-teal-50 text-teal-700 border-teal-200',
    assisted: 'bg-indigo-50 text-indigo-700 border-indigo-200',
    analytics: 'bg-amber-50 text-amber-700 border-amber-200',
    returning: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    unclassified: 'bg-gray-50 text-gray-600 border-gray-200',
};

const CategoryCard = ({ cat, bucket, active, onClick }) => {
    const Icon = cat.icon || HelpCircle;
    const count = bucket?.count || 0;
    return (
        <button
            type="button"
            onClick={onClick}
            aria-pressed={active}
            className={`text-left bg-white rounded-xl border p-4 transition-all hover:shadow-md hover:-translate-y-0.5 focus:outline-none ${
                active ? (cat.ringActive || 'border-gray-400 ring-2 ring-gray-200') : (cat.ring || 'border-gray-200')
            }`}
        >
            <div className="flex items-start gap-3">
                <div className={`p-2 rounded-lg flex-shrink-0 ${cat.tint || 'text-gray-500 bg-gray-50'}`}>
                    <Icon className="w-5 h-5" />
                </div>
                <div className="min-w-0">
                    <p className="text-xs font-semibold text-gray-700">{cat.label}</p>
                    <p className="text-xl font-bold text-gray-900 leading-tight mt-0.5">
                        {formatCurrency(bucket?.commission)}
                    </p>
                    <p className="text-[11px] text-gray-400 mt-0.5">
                        {count} sale{count === 1 ? '' : 's'}
                    </p>
                </div>
            </div>
            <p className="text-[11px] text-gray-500 mt-2 leading-snug">{cat.blurb}</p>
            <p className="text-[11px] font-semibold text-gray-400 mt-1">
                {count > 0 ? 'Click to see which sales →' : 'No sales yet'}
            </p>
        </button>
    );
};

const DomaInfluencedRevenue = ({ startDate, endDate }) => {
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState(null);
    const [selectedCategory, setSelectedCategory] = useState(null);

    useEffect(() => {
        let cancelled = false;
        const load = async () => {
            try {
                setLoading(true);
                const result = await fetchPortalAttributionAnalytics(startDate, endDate);
                if (!cancelled) setData(result);
            } catch (error) {
                console.error('Error loading DOMA attribution analytics:', error);
                if (!cancelled) toast.error('Failed to load DOMA-influenced revenue');
            } finally {
                if (!cancelled) setLoading(false);
            }
        };
        load();
        return () => { cancelled = true; };
    }, [startDate, endDate]);

    // Clear any open drill-down when the date range changes the underlying data.
    useEffect(() => { setSelectedCategory(null); }, [startDate, endDate]);

    if (loading && !data) {
        return (
            <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6">
                <div className="flex items-center gap-2 text-sm text-gray-500">
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    Loading DOMA-influenced revenue...
                </div>
            </div>
        );
    }

    const categories = data?.categories || {};
    const totals = data?.totals || { count: 0, revenue: 0, commission: 0 };
    const recentSales = data?.recent_sales || [];
    const unclassifiedBucket = categories.unclassified || { count: 0, revenue: 0, commission: 0 };

    // Share of total commission per category, for the stacked attribution bar.
    const totalCommission = Number(totals.commission) || 0;
    const allRows = [...CATEGORIES, UNCLASSIFIED];
    const shares = allRows
        .map((cat) => {
            const comm = Number(categories[cat.key]?.commission) || 0;
            return { cat, pct: totalCommission > 0 ? (comm / totalCommission) * 100 : 0, comm };
        })
        .filter((row) => row.pct > 0);

    const selectedMeta = allRows.find((c) => c.key === selectedCategory);
    const selectedSales = selectedCategory
        ? recentSales.filter((sale) => sale.category === selectedCategory)
        : [];

    const handleCategoryClick = (key) => {
        setSelectedCategory((prev) => (prev === key ? null : key));
    };

    return (
        <div className="bg-white rounded-xl border border-gray-200 p-4 sm:p-6 mb-6">
            <div className="flex items-start justify-between gap-4 mb-5">
                <div>
                    <div className="flex items-center gap-2">
                        <Globe className="w-5 h-5 text-[#55C5D0]" />
                        <h2 className="text-lg font-bold text-gray-900">DOMA Influenced Revenue</h2>
                    </div>
                    <p className="text-sm text-gray-500 mt-1">
                        Every sale the portal helped move — split by how DOMA contributed.
                    </p>
                </div>
                {loading ? <RefreshCw className="w-4 h-4 text-gray-400 animate-spin flex-shrink-0" /> : null}
            </div>

            {/* Headline */}
            <div className="rounded-2xl bg-gradient-to-r from-[#55C5D0]/10 to-emerald-50 border border-teal-100 p-5 mb-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                        <p className="text-xs font-semibold uppercase tracking-wider text-teal-700">
                            Total Influenced Commission
                        </p>
                        <p className="text-3xl font-extrabold text-gray-900 leading-tight mt-1">
                            {formatCurrency(totals.commission)}
                        </p>
                    </div>
                    <div>
                        <p className="text-xs font-semibold uppercase tracking-wider text-teal-700">
                            Transactions Influenced
                        </p>
                        <p className="text-3xl font-extrabold text-gray-900 leading-tight mt-1">
                            {(totals.count || 0).toLocaleString()}
                        </p>
                    </div>
                </div>

                {/* Stacked attribution bar */}
                {shares.length > 0 && (
                    <div className="mt-4">
                        <div className="flex h-2.5 w-full overflow-hidden rounded-full bg-white/60">
                            {shares.map(({ cat, pct }) => (
                                <div
                                    key={cat.key}
                                    className={cat.bar}
                                    style={{ width: `${pct}%` }}
                                    title={`${cat.label}: ${pct.toFixed(0)}%`}
                                />
                            ))}
                        </div>
                    </div>
                )}
            </div>

            {/* Commission Attribution — click a card to drill into its sales */}
            <h3 className="text-sm font-bold text-gray-700 mb-3">Commission Attribution</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
                {CATEGORIES.map((cat) => (
                    <CategoryCard
                        key={cat.key}
                        cat={cat}
                        bucket={categories[cat.key]}
                        active={selectedCategory === cat.key}
                        onClick={() => handleCategoryClick(cat.key)}
                    />
                ))}
            </div>

            {/* Drill-down: the sales that landed in the selected category */}
            {selectedCategory && (
                <div className="rounded-xl border border-gray-200 bg-gray-50/60 p-4 mb-6">
                    <div className="flex items-center justify-between gap-3 mb-3">
                        <h4 className="text-sm font-bold text-gray-800">
                            {selectedMeta?.label || CATEGORY_LABELS[selectedCategory]} — sales
                        </h4>
                        <button
                            type="button"
                            onClick={() => setSelectedCategory(null)}
                            className="flex items-center gap-1 text-xs font-medium text-gray-500 hover:text-gray-700"
                        >
                            <X className="w-3.5 h-3.5" /> Close
                        </button>
                    </div>

                    {selectedSales.length > 0 ? (
                        <div className="overflow-x-auto rounded-lg border border-gray-100 bg-white">
                            <table className="min-w-full text-sm">
                                <thead className="bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500">
                                    <tr>
                                        <th className="px-4 py-3 font-semibold">Property</th>
                                        <th className="px-4 py-3 font-semibold">Why</th>
                                        <th className="px-4 py-3 font-semibold">Sale Price</th>
                                        <th className="px-4 py-3 font-semibold">Commission</th>
                                        <th className="px-4 py-3 font-semibold">Sold</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100">
                                    {selectedSales.map((sale) => (
                                        <tr key={`${sale.source}-${sale.property_id}-${sale.sold_date}`} className="hover:bg-gray-50/80 align-top">
                                            <td className="px-4 py-3 font-medium text-gray-900 max-w-[16rem] truncate">
                                                {sale.address || '—'}
                                                {sale.agent_name && (
                                                    <span className="block text-[11px] font-normal text-gray-400">
                                                        {sale.agent_name}
                                                    </span>
                                                )}
                                            </td>
                                            <td className="px-4 py-3 text-gray-500 max-w-[22rem]">
                                                <span className="block">{sale.attribution_reason}</span>
                                                {Array.isArray(sale.signals) && sale.signals.length > 0 && (
                                                    <span className="block text-[11px] text-gray-400 mt-0.5">
                                                        {sale.signals.join(' · ')}
                                                    </span>
                                                )}
                                            </td>
                                            <td className="px-4 py-3 text-gray-900 whitespace-nowrap">
                                                {formatCurrency(sale.sold_price)}
                                            </td>
                                            <td className="px-4 py-3 text-emerald-700 font-semibold whitespace-nowrap">
                                                {formatCurrency(sale.commission)}
                                            </td>
                                            <td className="px-4 py-3 text-gray-600 whitespace-nowrap">
                                                {sale.sold_date || '—'}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    ) : (
                        <p className="text-sm text-gray-500">
                            No sales in this category for the selected date range.
                        </p>
                    )}
                </div>
            )}

            {unclassifiedBucket.count > 0 && (
                <button
                    type="button"
                    onClick={() => handleCategoryClick('unclassified')}
                    className="block w-full text-left text-xs text-gray-500 bg-gray-50 hover:bg-gray-100 rounded-lg px-4 py-2.5 transition-colors"
                >
                    {unclassifiedBucket.count} portal-attributed sale
                    {unclassifiedBucket.count === 1 ? '' : 's'} ({formatCurrency(unclassifiedBucket.commission)} commission)
                    {unclassifiedBucket.count === 1 ? ' hasn’t' : ' haven’t'} been categorised yet, so
                    {unclassifiedBucket.count === 1 ? ' it’s' : ' they’re'} counted in the total but not in a
                    category. Open the sold property and pick a DOMA category to classify
                    {unclassifiedBucket.count === 1 ? ' it' : ' them'}. <span className="font-semibold">Click to view →</span>
                </button>
            )}

            {totals.count === 0 && (
                <p className="text-sm text-gray-500 bg-gray-50 rounded-lg px-4 py-3">
                    No portal-attributed sales in this date range yet. Agents can tick
                    &ldquo;Sold through the Collings portal&rdquo; when marking a property as sold.
                </p>
            )}
        </div>
    );
};

export default DomaInfluencedRevenue;
